import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { CfsApi, KaseApi, TaskApi } from '@/api';
import { InternalAffairsApi } from '@/api/api.internal-affairs';
import { IcDotVert } from '@/assets/images';
import { Page, PageHeader } from '@/components';
import { useSearchPersist } from '@/components/DataGrid/hooks/useSearchPersist';
import { ExportAsPDFModal } from '@/components/Modals/InternalAffairsModal/ExportAsPdf';
import { DeleteInternalAffairsCaseModal } from '@/components/Modals/InternalAffairsModal/InternalAffairsDeleteModal';
import { SendEmailModal } from '@/components/Modals/InternalAffairsModal/SendEmail';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/TailwindComponents';
import { LocalStorageItems } from '@/constants';
import { withLoader, WithLoaderProps } from '@/hocs';
import withUnsavedChangesWarning, {
  WithUnsavedChangesWarningProps,
} from '@/hocs/withUnsavedChangesWarning';
import {
  IIACase,
  IACaseType,
  IACaseStatus,
  ICFS,
  IKase,
  ITask,
  IFile,
  FileOrigin,
} from '@/models';
import { validateRoute } from '@/services';
import { uploadFiles } from '@/services/service.file';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';

import { internalAffairsNavTabs } from './data';
import { generateSingleCasePDF } from './ia.utils';

export type InternalAffairsFormValues = {
  caseType: IACaseType | '';
  status: IACaseStatus;
  caseDetails: string;
  investigationNotes: string;
  officerDetails: string;
  findingsRecommendations: string;
  assignedOfficers: string[];
  files: IFile[];
};

const InternalAffairsAddEditPage: FC<
  WithLoaderProps & WithUnsavedChangesWarningProps
> = ({ showLoader, hideLoader }) => {
  const { InternalAffairsId } = useParams<{ InternalAffairsId: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { navigateWithSearch, searchQuery } = useSearchPersist();

  // Derive active tab from URL path
  const pathParts = pathname.split('/');
  const activeTab = pathParts[pathParts.length - 1];

  const { updateToast } = useToastStore();
  const { account, user } = useUserStore();
  const [caseData, setCaseData] = useState<IIACase | null>(null);
  const [isAddingPerson, setIsAddingPerson] = useState(false);
  const [prefetchedSearchData, setPrefetchedSearchData] = useState<
    ((ICFS | IKase | ITask) & { recordType: 'cfs' | 'kase' | 'task' })[]
  >([]);

  const methods = useForm<InternalAffairsFormValues>({
    defaultValues: {
      caseType: '',
      status: IACaseStatus.OPEN,
      caseDetails: '',
      investigationNotes: '',
      officerDetails: '',
      findingsRecommendations: '',
      assignedOfficers: [],
      files: [],
    },
    mode: 'all',
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  // Redirect to overview tab if no tab is selected
  useEffect(() => {
    const validTabs = internalAffairsNavTabs.map((t) => t.path);
    if (!validTabs.includes(activeTab)) {
      navigate(
        { pathname: 'overview', search: searchQuery.replace('?', '') },
        { replace: true },
      );
    }
  }, [activeTab, navigate]);

  useEffect(() => {
    const fetchCase = async () => {
      if (!InternalAffairsId) return;

      showLoader();

      try {
        const response = await InternalAffairsApi.getOne(InternalAffairsId);
        const data = response.data;
        setCaseData(data);

        const formData: InternalAffairsFormValues = {
          caseType: data.caseType,
          status: data.status,
          caseDetails: data.caseDetails || '',
          investigationNotes: data.investigationNotes || '',
          officerDetails: data.officerDetails || '',
          findingsRecommendations: data.findingsAndRecommendations || '',
          assignedOfficers:
            data.assignedOfficers?.map((o) =>
              typeof o === 'string' ? o : o._id,
            ) || [],
          files: data.files || [],
        };

        methods.reset(formData);
      } finally {
        hideLoader();
      }
    };

    fetchCase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [InternalAffairsId]);

  // Prefetch related search data so it's ready when user navigates to the Related Records tab
  useEffect(() => {
    const prefetchSearchData = async () => {
      try {
        const params = {
          sort: JSON.stringify({ createdAt: -1 }),
          limit: 50,
          filter: '',
        };

        const currentAgencyId = localStorage.getItem(
          LocalStorageItems.CurrentAgencyId,
        );
        const canGetKase =
          !!currentAgencyId &&
          validateRoute('/cases', user || undefined, account || undefined);

        const [resCfs, resKase, resTask] = await Promise.all([
          CfsApi.list(params),
          canGetKase ? KaseApi.list(params) : undefined,
          currentAgencyId ? TaskApi.getTasks(params) : undefined,
        ]);

        const cfsItems = resCfs.data.docs.map((cfs: ICFS) => ({
          ...cfs,
          recordType: 'cfs' as const,
        }));
        const kaseItems = resKase
          ? resKase.data.results.map((kaseItem: IKase) => ({
              ...kaseItem,
              recordType: 'kase' as const,
            }))
          : [];
        const taskItems = resTask
          ? (resTask.data.docs || []).map((task: ITask) => ({
              ...task,
              recordType: 'task' as const,
            }))
          : [];

        setPrefetchedSearchData([...cfsItems, ...kaseItems, ...taskItems]);
      } catch {
        // Silently fail - RelatedSearch will fetch on mount as fallback
      }
    };

    prefetchSearchData();
  }, []);

  const onSubmit = methods.handleSubmit(async (formValues) => {
    try {
      showLoader();

      // Upload any new files (files with a File object)
      let uploadedFiles: IFile[] = formValues.files || [];
      const newFiles = uploadedFiles.filter((f) => f.file);
      const existingFiles = uploadedFiles
        .filter((f) => !f.file && f.url)
        .map((f) => ({
          ...f,
          uploadedBy:
            typeof f.uploadedBy === 'string'
              ? f.uploadedBy
              : (f.uploadedBy as any)?._id,
        }));

      if (newFiles.length > 0) {
        const uploaded = await uploadFiles(newFiles);
        uploadedFiles = [...uploaded, ...existingFiles];
      } else {
        uploadedFiles = existingFiles;
      }

      if (InternalAffairsId && caseData) {
        // Update existing case
        await InternalAffairsApi.update(InternalAffairsId, {
          caseType: formValues.caseType || undefined,
          status: formValues.status,
          caseDetails: formValues.caseDetails,
          officerDetails: formValues.officerDetails,
          investigationNotes: formValues.investigationNotes,
          findingsAndRecommendations: formValues.findingsRecommendations,
        });

        // Sync assigned officers via dedicated endpoints
        const originalOfficerIds = (caseData.assignedOfficers || []).map((o) =>
          typeof o === 'string' ? o : o._id,
        );
        const currentOfficerIds = formValues.assignedOfficers;

        const toAdd = currentOfficerIds.filter(
          (id) => !originalOfficerIds.includes(id),
        );
        const toRemove = originalOfficerIds.filter(
          (id) => !currentOfficerIds.includes(id),
        );

        if (toAdd.length > 0) {
          await InternalAffairsApi.addAssignedOfficers(
            InternalAffairsId,
            toAdd,
          );
        }
        if (toRemove.length > 0) {
          await InternalAffairsApi.removeAssignedOfficers(
            InternalAffairsId,
            toRemove,
          );
        }

        // Update files if there are any changes
        if (uploadedFiles.length > 0 || (caseData.files?.length || 0) > 0) {
          await InternalAffairsApi.updateFiles(
            InternalAffairsId,
            uploadedFiles,
          );
        }
      } else {
        // Create new case
        if (!formValues.caseType) {
          return; // Should not happen due to validation
        }
        const response = await InternalAffairsApi.create({
          caseType: formValues.caseType,
          caseDetails: formValues.caseDetails,
          officerDetails: formValues.officerDetails,
          investigationNotes: formValues.investigationNotes,
          findingsAndRecommendations: formValues.findingsRecommendations,
          assignedOfficers: formValues.assignedOfficers,
        });

        // Update files for the new case if any
        if (uploadedFiles.length > 0 && response.data._id) {
          await InternalAffairsApi.updateFiles(
            response.data._id,
            uploadedFiles,
          );
        }
      }

      updateToast({
        type: ConfirmTypes.SUCCESS,
        open: true,
        message: InternalAffairsId
          ? 'Case updated successfully'
          : 'Case created successfully',
      });

      navigate('/tools/internal-affairs');
    } catch (error) {
      updateToast({
        open: true,
        message: InternalAffairsId
          ? 'Failed to update case'
          : 'Failed to create case',
      });
    } finally {
      hideLoader();
    }
  });

  const handleTabChange = (tabPath: string) => {
    if (isAddingPerson) return; // Prevent tab change while adding person
    navigate(
      { pathname: tabPath, search: searchQuery.replace('?', '') },
      { replace: true },
    );
  };

  const handleExport = () => {
    setIsExportModalOpen(true);
  };

  const handleEmail = () => {
    setIsSendEmailModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  return (
    <Page
      title={
        InternalAffairsId
          ? 'Edit Internal Affairs Case'
          : 'Add Internal Affairs Case'
      }
    >
      <PageHeader
        title={
          InternalAffairsId
            ? 'Edit Internal Affairs Case'
            : 'Add Internal Affairs Case'
        }
        onBack={() => navigateWithSearch('/tools/internal-affairs')}
      >
        <div className="flex gap-4">
          <Button
            type="button"
            className="rounded-md bg-white"
            variant="outline"
            onClick={() => navigateWithSearch('/tools/internal-affairs')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            className="rounded-md"
            onClick={onSubmit}
          >
            Save
          </Button>

          {/* Show kebab menu in edit mode */}
          {InternalAffairsId && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="cursor-pointer bg-white border border-grey-20 rounded-md px-4 py-3">
                  <IcDotVert />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={handleExport}
                  className="cursor-pointer font-medium"
                >
                  Export
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleEmail}
                  className="cursor-pointer font-medium"
                >
                  Email
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleDelete}
                  className="text-red-600 cursor-pointer font-medium"
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </PageHeader>

      <FormProvider {...methods}>
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList className="bg-transparent h-auto p-0 border-b !border-[#E7EAEC] dark:!border-[#4B5563] rounded-none w-full justify-start">
            {internalAffairsNavTabs.map((tab) => (
              <TabsTrigger
                key={tab.path}
                value={tab.path}
                disabled={isAddingPerson}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#EE3E5C] data-[state=active]:bg-transparent data-[state=active]:shadow-none mr-2 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <Outlet
          context={{
            caseData,
            setCaseData,
            showLoader,
            hideLoader,
            isAddingPerson,
            setIsAddingPerson,
            prefetchedSearchData,
          }}
        />
      </FormProvider>

      <DeleteInternalAffairsCaseModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={async (reason?: string) => {
          if (InternalAffairsId && reason) {
            try {
              await InternalAffairsApi.delete(InternalAffairsId, reason);
              updateToast({
                type: ConfirmTypes.SUCCESS,
                open: true,
                message: 'IA Case deleted successfully',
              });
              navigate('/tools/internal-affairs');
            } catch (error) {
              updateToast({
                open: true,
                message: 'Failed to delete IA case',
              });
            }
          }
          setIsDeleteModalOpen(false);
        }}
        caseData={
          caseData
            ? {
                caseId: caseData.caseId,
                caseType: caseData.caseType,
                status: caseData.status,
                createdBy:
                  typeof caseData.createdBy === 'string'
                    ? caseData.createdBy
                    : (caseData.createdBy as any)?.fullName || 'Unknown',
              }
            : null
        }
      />

      <SendEmailModal
        open={isSendEmailModalOpen}
        onClose={() => setIsSendEmailModalOpen(false)}
        loading={emailSending}
        onConfirm={async (emailData) => {
          if (!InternalAffairsId) return;
          setEmailSending(true);
          showLoader();
          try {
            const pdfBlob = await generateSingleCasePDF(
              InternalAffairsId,
              'blob',
              {
                recipient: emailData.recipient,
                reason: emailData.reason,
              },
            );
            if (!pdfBlob || !(pdfBlob instanceof Blob)) {
              updateToast({
                open: true,
                message: 'Failed to generate PDF for email',
                type: ConfirmTypes.ERROR,
              });
              return;
            }

            const fileName = `${
              caseData?.caseId || 'IA_Case'
            } - Emailed on ${new Date().toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            })}.pdf`;
            const file = new File([pdfBlob], fileName, {
              type: 'application/pdf',
            });

            const uploadedFiles = await uploadFiles([
              {
                name: file.name,
                size: file.size,
                mimeType: file.type,
                origin: FileOrigin.EMAIL,
                file,
              },
            ]);

            if (uploadedFiles?.length > 0) {
              const uploadedFile = uploadedFiles[0];
              await InternalAffairsApi.emailFiles(InternalAffairsId, {
                emailAddress: emailData.recipientEmail,
                subject: 'Internal Affairs Report',
                emailBody: emailData.emailBody,
                files: [{ ...uploadedFile, uploadedBy: undefined }],
              });

              updateToast({
                open: true,
                message: 'Email sent successfully!',
                type: ConfirmTypes.SUCCESS,
              });
            }

            setIsSendEmailModalOpen(false);
          } catch (error: any) {
            updateToast({
              open: true,
              message: error?.message || 'Failed to send email',
              type: ConfirmTypes.ERROR,
            });
          } finally {
            setEmailSending(false);
            hideLoader();
          }
        }}
        caseData={
          caseData
            ? {
                caseId: caseData.caseId,
                caseType: caseData.caseType,
                status: caseData.status,
                createdBy:
                  typeof caseData.createdBy === 'string'
                    ? caseData.createdBy
                    : (caseData.createdBy as any)?.fullName || 'Unknown',
              }
            : null
        }
      />

      <ExportAsPDFModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onConfirm={async (exportData) => {
          if (InternalAffairsId) {
            await generateSingleCasePDF(
              InternalAffairsId,
              'download',
              exportData,
            );
          }
          setIsExportModalOpen(false);
        }}
        caseData={
          caseData
            ? {
                caseId: caseData.caseId,
                caseType: caseData.caseType,
                status: caseData.status,
                createdBy:
                  typeof caseData.createdBy === 'string'
                    ? caseData.createdBy
                    : (caseData.createdBy as any)?.fullName || 'Unknown',
              }
            : null
        }
      />
    </Page>
  );
};

export default withLoader(
  withUnsavedChangesWarning(InternalAffairsAddEditPage),
);
